import { motion } from "framer-motion";
import { ArrowDown, Sun } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { usePagesContent } from "@/data/pages";

const FALLBACK_LOGO_URL =
  "https://customer-assets.emergentagent.com/job_heels-dance-journey/artifacts/qnhcpxfy_ChatGPT%20Image%2017%20giu%202026%2C%2020_43_04.png";

export const Hero = () => {
  const navigate = useNavigate();
  const { hero } = usePagesContent();
  const logoUrl = hero.logo_url || FALLBACK_LOGO_URL;

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen overflow-hidden flex items-end sun-bg"
    >
      {/* Soft azure halo behind the round logo badge — desktop only */}
      <div className="hidden sm:block absolute top-[6%] right-[4%] sm:right-[7%] w-[24vmin] sm:w-[34vmin] h-[24vmin] sm:h-[34vmin] z-[4] pointer-events-none">
        <div
          className="absolute inset-[-25%] rounded-full blur-3xl"
          style={{
            background:
              "radial-gradient(circle at 50% 50%, rgba(122, 184, 214, 0.7) 0%, rgba(168, 213, 229, 0.4) 40%, rgba(122, 184, 214, 0) 75%)",
          }}
        />
      </div>

      {/* Round white logo badge — desktop only (mobile uses inline below) */}
      <div
        className="hidden sm:block absolute top-[6%] right-[4%] sm:right-[7%] w-[24vmin] sm:w-[34vmin] h-[24vmin] sm:h-[34vmin] z-[5] pointer-events-none rounded-full bg-white bg-no-repeat bg-center shadow-[0_20px_50px_-15px_rgba(79,143,168,0.45)] ring-1 ring-white/80"
        style={{
          backgroundImage: `url('${logoUrl}')`,
          backgroundSize: "82%",
        }}
        role="img"
        aria-label="Mantel — Summer Heels Experience"
      />

      {/* Soft azure cloud blobs */}
      <div className="absolute top-[35%] left-[-8%] w-[55vmin] h-[35vmin] rounded-full bg-[#7AB8D6]/40 blur-3xl" />
      <div className="absolute bottom-[10%] left-[20%] w-[35vmin] h-[25vmin] rounded-full bg-[#7AB8D6]/25 blur-3xl" />
      <div className="absolute top-[60%] right-[25%] w-[28vmin] h-[20vmin] rounded-full bg-[#A8D5E5]/35 blur-3xl" />

      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 lg:px-10 pb-24 pt-32">
        {/* Mobile-only inline logo */}
        <div className="sm:hidden mb-8 flex justify-center">
          <div
            className="w-32 h-32 rounded-full bg-white bg-no-repeat bg-center shadow-[0_20px_50px_-15px_rgba(79,143,168,0.45)] ring-1 ring-white/80 relative"
            style={{
              backgroundImage: `url('${logoUrl}')`,
              backgroundSize: "82%",
            }}
            role="img"
            aria-label="Mantel — Summer Heels Experience"
          >
            <div
              className="absolute inset-[-30%] rounded-full blur-3xl -z-10"
              style={{
                background:
                  "radial-gradient(circle at 50% 50%, rgba(122, 184, 214, 0.7) 0%, rgba(168, 213, 229, 0.4) 40%, rgba(122, 184, 214, 0) 75%)",
              }}
            />
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          className="flex items-center gap-3 mb-8"
        >
          <Sun size={18} className="text-[#4F8FA8]" strokeWidth={1.5} />
          <span className="overline text-[#4F8FA8]">{hero.eyebrow}</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.15, ease: "easeOut" }}
          className="font-serif-display text-[3.25rem] sm:text-7xl lg:text-[9rem] leading-[0.95] tracking-tight max-w-5xl text-[#2A1810]"
        >
          {hero.title_line1}
          <br />
          <span className="font-serif-italic text-[#A0522D]">
            {hero.title_italic}
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.35 }}
          className="mt-10 max-w-2xl text-base sm:text-lg text-[#6E5B4A] leading-relaxed"
          style={{ whiteSpace: "pre-line" }}
        >
          {hero.description}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.55 }}
          className="mt-12 flex flex-wrap gap-4"
        >
          <button
            data-testid="hero-cta-discover"
            onClick={() => scrollTo("workshops")}
            className="btn-primary"
          >
            {hero.cta_primary}
          </button>
          <button
            data-testid="hero-cta-register"
            onClick={() => navigate("/register")}
            className="btn-azzurro"
          >
            {hero.cta_secondary}
          </button>
        </motion.div>

        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 1 }}
          onClick={() => scrollTo("experience")}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#6E5B4A] hover:text-[#A0522D] transition flex flex-col items-center gap-2"
          aria-label="scroll"
        >
          <span className="overline text-[10px]">Scroll</span>
          <ArrowDown size={18} className="animate-bounce" />
        </motion.button>
      </div>
    </section>
  );
};
